import React, { useMemo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import "./MuroCategorias.css";

/* ========= helpers responsive img ========= */
const addW = (url, w) =>
  url.includes("?") ? `${url}&w=${w}&auto=format` : `${url}?w=${w}&auto=format`;

const srcSet = (url, widths = [320, 480, 640, 768, 960, 1200]) =>
  widths.map((w) => `${addW(url, w)} ${w}w`).join(", ");

const sizes = "(max-width: 640px) 90vw, (max-width: 1024px) 45vw, 360px";

const tiny = (url) =>
  url.includes("?")
    ? `${url}&w=24&blur=30&auto=format`
    : `${url}?w=24&blur=30&auto=format`;

/* ========= componente ========= */
export default function MuroCategorias({ categorias = [] }) {
  // 9 primeros
  const items = categorias.slice(0, 9);
  const baseTop = items.slice(0, 5);
  const baseBot = items.slice(5, 9);

  // repetimos 3× para lograr bucle largo
  const top = useMemo(() => [...baseTop, ...baseTop, ...baseTop], [baseTop]);
  const bot = useMemo(() => [...baseBot, ...baseBot, ...baseBot], [baseBot]);

  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const wrapRef = useRef(null);

  // estado de animación (un solo rAF para las dos filas)
  const animRef = useRef({
    // layout
    seg1: 1, // ancho de 1 segmento (fila top)
    seg2: 1, // idem bottom
    vp1: 1,
    vp2: 1,
    off1: 0, // offset actual
    off2: 0,

    // visibilidad / control
    playing: false,
    visible: true,
    inView: false,
    reduce: false,

    // velocidades (ramp-up)
    speed1: 0,
    target1: 30, // px/s
    dir1: -1,
    speed2: 0,
    target2: 24,
    dir2: +1,
    ramp: 0, // 0..1

    // diagnostico
    imagesReady: 0
  });

  /* ---------- medición de segmentos ---------- */
  const measure = () => {
    const st = animRef.current;
    const row1 = row1Ref.current;
    const row2 = row2Ref.current;
    if (!row1 || !row2) return;

    const segOf = (row, count) => {
      let seg = 0;
      for (let i = 0; i < Math.min(count, row.children.length); i++) {
        const el = row.children[i];
        const rect = el.getBoundingClientRect();
        const mr = parseFloat(getComputedStyle(el).marginRight || "0") || 0;
        seg += rect.width + mr;
      }
      return Math.max(1, seg);
    };

    st.seg1 = segOf(row1, baseTop.length);
    st.seg2 = segOf(row2, baseBot.length);

    const vp1 = row1.parentElement ? row1.parentElement.clientWidth : window.innerWidth;
    const vp2 = row2.parentElement ? row2.parentElement.clientWidth : window.innerWidth;
    st.vp1 = Math.max(1, vp1);
    st.vp2 = Math.max(1, vp2);

    // normalizamos offset para que no salte
    const norm = (off, seg, vp) => {
      const EPS = 1.5;
      const min = -2 * seg + vp + EPS;
      const max = -vp - EPS;
      while (off < min) off += seg;
      while (off > max) off -= seg;
      return off;
    };

    st.off1 = norm(st.off1, st.seg1, st.vp1);
    st.off2 = norm(st.off2, st.seg2, st.vp2);

    row1.style.transform = `translate3d(${st.off1}px,0,0)`;
    row2.style.transform = `translate3d(${st.off2}px,0,0)`;
  };

  const queueMeasure = () => {
    // medir en el siguiente frame
    requestAnimationFrame(measure);
  };

  /* ---------- observers: visibilidad y viewport ---------- */
  useEffect(() => {
    const st = animRef.current;

    // reduce-motion respeta accesibilidad
    st.reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // intersection (arranque instantáneo al entrar)
    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        st.inView = !!e?.isIntersecting;
        if (st.inView) {
          st.playing = true;
          st.ramp = 0; // arranca desde 0 a target en 0.35s
          queueMeasure();
        }
      },
      { root: null, threshold: 0.05 }
    );
    if (wrapRef.current) io.observe(wrapRef.current);

    // page visibility (pausa en background)
    const onVis = () => {
      st.visible = document.visibilityState === "visible";
      if (st.visible) {
        st.ramp = Math.min(st.ramp, 0.85); // retoma suave si volvés
        queueMeasure();
      }
    };
    document.addEventListener("visibilitychange", onVis);

    // resize
    const ro1 = new ResizeObserver(queueMeasure);
    const ro2 = new ResizeObserver(queueMeasure);
    if (row1Ref.current) ro1.observe(row1Ref.current);
    if (row2Ref.current) ro2.observe(row2Ref.current);
    const onWinResize = () => queueMeasure();
    window.addEventListener("resize", onWinResize, { passive: true });

    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", onVis);
      ro1.disconnect();
      ro2.disconnect();
      window.removeEventListener("resize", onWinResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- animación (un solo rAF) ---------- */
  useEffect(() => {
    let raf = 0;
    let last = 0;

    const step = (now) => {
      raf = requestAnimationFrame(step);
      const st = animRef.current;
      if (!last) last = now;
      const dt = (now - last) / 1000; // segundos
      last = now;

      const paused = !st.playing || st.reduce || !st.visible || !st.inView;
      if (paused) return;

      // ramp up de 0 a target ~350ms (sin tirón)
      if (st.ramp < 1) {
        st.ramp = Math.min(1, st.ramp + dt / 0.35);
        st.speed1 = st.target1 * st.ramp;
        st.speed2 = st.target2 * st.ramp;
      }

      const move = (off, dir, speed, seg, vp) => {
        const EPS = 1.5;
        const min = -2 * seg + vp + EPS;
        const max = -vp - EPS;
        off += dir * speed * dt;
        while (off < min) off += seg;
        while (off > max) off -= seg;
        return off;
      };

      st.off1 = move(st.off1, st.dir1, st.speed1, st.seg1, st.vp1);
      st.off2 = move(st.off2, st.dir2, st.speed2, st.seg2, st.vp2);

      if (row1Ref.current) row1Ref.current.style.transform = `translate3d(${st.off1}px,0,0)`;
      if (row2Ref.current) row2Ref.current.style.transform = `translate3d(${st.off2}px,0,0)`;
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  /* ---------- imágenes: contamos y re-medimos, no bloquea play ---------- */
  useEffect(() => {
    let loaded = 0;
    const need = 2;
    const onReady = () => {
      loaded += 1;
      animRef.current.imagesReady = loaded;
      if (loaded <= need) queueMeasure();
    };

    const imgs = (wrapRef.current || document).querySelectorAll(
      ".muro-card img"
    );
    imgs.forEach((img) => {
      if (img.complete) onReady();
      else img.addEventListener("load", onReady, { once: true });
    });

    return () => {
      imgs.forEach((img) => img.removeEventListener("load", onReady));
    };
  }, []);

  /* ---------- hover pausa (retoma suave) ---------- */
  const onEnter = () => {
    animRef.current.playing = false;
  };
  const onLeave = () => {
    const st = animRef.current;
    st.playing = true;
    st.ramp = Math.min(st.ramp, 0.85);
  };

  return (
    <section className="muro-wrap" ref={wrapRef}>
      <h2 className="home-subtitle">Explorar por categoría</h2>

      <div
        className="muro-rows"
        aria-label="Categorías destacadas"
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
      >
        {/* Fila 1 */}
        <div className="row-viewport r1">
          <div className="row row1" ref={row1Ref}>
            {top.map((c, i) => (
              <Link to={c.url} key={`t-${i}`} className="muro-card" title={c.titulo}>
                <picture>
                  <source type="image/webp" srcSet={srcSet(c.imagen)} sizes={sizes} />
                  <img
                    src={addW(c.imagen, 480)}
                    srcSet={srcSet(c.imagen)}
                    sizes={sizes}
                    alt={c.titulo}
                    draggable="false"
                    onDragStart={(e) => e.preventDefault()}
                    onContextMenu={(e) => e.preventDefault()}
                    loading={i < 2 ? "eager" : "lazy"}
                    fetchpriority={i < 2 ? "high" : "low"}
                    decoding="async"
                    width={360}
                    height={220}
                    style={{
                      background: `center/cover no-repeat url("${tiny(c.imagen)}")`,
                    }}
                  />
                </picture>
                <div className="muro-mask" />
                <div className="muro-center">{c.titulo}</div>
              </Link>
            ))}
          </div>
        </div>

        {/* Fila 2 */}
        <div className="row-viewport r2">
          <div className="row row2" ref={row2Ref}>
            {bot.map((c, i) => (
              <Link to={c.url} key={`b-${i}`} className="muro-card" title={c.titulo}>
                <picture>
                  <source type="image/webp" srcSet={srcSet(c.imagen)} sizes={sizes} />
                  <img
                    src={addW(c.imagen, 480)}
                    srcSet={srcSet(c.imagen)}
                    sizes={sizes}
                    alt={c.titulo}
                    draggable="false"
                    onDragStart={(e) => e.preventDefault()}
                    onContextMenu={(e) => e.preventDefault()}
                    loading={i < 2 ? "eager" : "lazy"}
                    fetchpriority={i < 2 ? "high" : "low"}
                    decoding="async"
                    width={360}
                    height={220}
                    style={{
                      background: `center/cover no-repeat url("${tiny(c.imagen)}")`,
                    }}
                  />
                </picture>
                <div className="muro-mask" />
                <div className="muro-center">{c.titulo}</div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
